/**
 * UserMenu — Iteration 2: Bottom-Slot Variante (Spec §2.1).
 *
 * Sitzt jetzt im Sidebar-Footer (h-14). Akzeptiert `collapsed`:
 *   - Expanded: Avatar (32px) + Name + Email + ChevronUp rechts.
 *   - Collapsed: nur Avatar zentriert, Tooltip rechts mit Name.
 *
 * Click öffnet DropdownMenu nach OBEN (`side="top"`):
 *   - Profil
 *   - Einstellungen
 *   - Theme-Submenu (Hell / Dunkel / System mit Check für aktive)
 *   - Tastenkürzel anzeigen (öffnet ShortcutsCheatsheet)
 *   - Abmelden
 */
import {
  ChevronUp,
  Keyboard,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
  User as UserIcon,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useThemeStore, type Theme } from "@/stores/theme";
import { useShortcutsCheatsheet } from "@/components/shared";
import { initials } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { PLATFORM_HUB_URL } from "@/lib/auth";

export interface UserMenuProps {
  /** Layout-Modus: collapsed = nur Avatar mit Tooltip; expanded = Avatar + Name + Email. */
  collapsed?: boolean;
}

interface ThemeOption {
  value: Theme;
  label: string;
  icon: typeof Sun;
}

const THEME_OPTIONS: ThemeOption[] = [
  { value: "light", label: "Hell", icon: Sun },
  { value: "dark", label: "Dunkel", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

export function UserMenu({ collapsed = false }: UserMenuProps = {}) {
  const { user } = useAuth();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const { setOpen: setCheatsheetOpen } = useShortcutsCheatsheet();

  const name = user?.name || "Unbekannt";
  const email = user?.email || "";
  const userInitials = (initials(name) || "?").toUpperCase();

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={collapsed ? 80 : 700}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Benutzer-Menü: ${name}`}
                className={cn(
                  "group flex h-11 items-center gap-2.5 rounded-md text-text-primary",
                  "transition-colors duration-fast ease-out-quint",
                  "hover:bg-text-primary/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  collapsed ? "w-10 justify-center px-0" : "w-full px-2",
                )}
              >
                <Avatar className="h-8 w-8 shrink-0 ring-1 ring-border-default">
                  <AvatarFallback className="bg-brand-tint text-tiny font-semibold text-brand-700">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <>
                    <span className="flex min-w-0 flex-1 flex-col items-start">
                      <span className="w-full truncate text-body-strong leading-tight">
                        {name}
                      </span>
                      {email && (
                        <span className="w-full truncate text-tiny text-text-tertiary">
                          {email}
                        </span>
                      )}
                    </span>
                    <ChevronUp className="h-3.5 w-3.5 shrink-0 text-text-tertiary group-hover:text-text-secondary" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" sideOffset={8}>
              {name}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-64"
      >
        <DropdownMenuLabel className="py-2">
          <div className="text-body-strong text-text-primary">{name}</div>
          {email && (
            <div className="text-tiny text-text-tertiary">{email}</div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem>
          <UserIcon className="h-4 w-4" /> Profil
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Settings className="h-4 w-4" /> Einstellungen
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Sun className="h-4 w-4" />
            <span>Theme</span>
            <span className="ml-auto text-tiny text-text-tertiary">
              {THEME_OPTIONS.find((o) => o.value === theme)?.label}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {THEME_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = theme === opt.value;
              return (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => setTheme(opt.value)}
                  className={cn(active && "text-brand-600")}
                >
                  <Icon className="h-4 w-4" />
                  <span>{opt.label}</span>
                  {active && (
                    <span className="ml-auto text-tiny text-brand-600">✓</span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem onClick={() => setCheatsheetOpen(true)}>
          <Keyboard className="h-4 w-4" />
          <span>Tastenkürzel anzeigen</span>
          <span className="ml-auto text-tiny text-text-tertiary">?</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => (window.location.href = `${PLATFORM_HUB_URL}/logout`)}
          className="text-status-rejected"
        >
          <LogOut className="h-4 w-4" /> Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
